import { useCallback, useEffect, useState } from 'react'

import { errorText } from '../../lib/offsite'
import { PeriodFilter } from '../../lib/PeriodFilter'
import type { PeriodValue } from '../../lib/PeriodFilter'
import { useSession } from '../../lib/SessionContext'
import { SectionHead } from '../offsite/parts'

/* ============================================================================
   สรุป OT ทั้งบริษัทรายเดือน
   API: backend/src/routes/adminOtSummary.js · lib/otSummary.js

   แทนแท็บ "รายงานโอที" ของ frmReportAll และหน้า "ประวัติขอ OT" ของ frontend2026

   ── ⚠️ ทำไมต้องเชื่อตัวเลขชุดนี้ ────────────────────────────────────────────
   ชั่วโมง OT ผูกกับเงิน และตาราง OT มีกับดักหลายชั้น (แถว TEMP ซ้ำเป๊ะ ·
   ROW_ID ซ้ำข้ามตาราง · ร่างที่เวลาทับกับใบที่อนุมัติแล้ว)
   ตัวเลขในหน้านี้ตรวจแล้วว่าตรงกับ lib/ot.js ทุกคน 5 เดือน (222 คน ไม่ต่างเลย)
   ============================================================================ */

type Row = {
  empCode: string
  empName: string
  docs: number
  approvedDocs: number
  rejectedDocs: number
  minutes: number
  hours: number
  approvedHours: number
  byRate: Record<string, number>
  anyClosed: boolean
}

type Result = {
  month: string
  range: { from: string; to: string }
  items: Row[]
  totals: { people: number; docs: number; hours: number; approvedHours: number }
}

const BASE = '/api/admin/ot-summary'
const nf = new Intl.NumberFormat('th-TH')
const hr = (n: number) => (n ? nf.format(n) : '-')

export default function OtSummary() {
  const { call } = useSession()

  const [period, setPeriod] = useState<PeriodValue | null>(null)
  const [status, setStatus] = useState('')
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')

  const [data, setData] = useState<Result | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!period?.from || !period.to) return
    setLoading(true)
    try {
      const qs = new URLSearchParams({ from: period.from, to: period.to })
      if (status) qs.set('status', status)
      if (q) qs.set('q', q)
      setData(await call<Result>(`${BASE}?${qs}`))
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [call, period?.from, period?.to, status, q])

  useEffect(() => {
    void load()
  }, [load])

  const rows = data?.items ?? []

  const exportCsv = () => {
    const head = ['รหัส', 'ชื่อ', 'ใบทั้งหมด', 'อนุมัติ', 'ไม่อนุมัติ', 'ชั่วโมงรวม', 'ชั่วโมงที่อนุมัติ', 'x1', 'x1.5', 'x2', 'x3']
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const body = rows.map((r) =>
      [r.empCode, r.empName, r.docs, r.approvedDocs, r.rejectedDocs, r.hours, r.approvedHours,
       r.byRate['1'], r.byRate['1.5'], r.byRate['2'], r.byRate['3']].map(esc).join(','),
    )
    // BOM ให้ Excel อ่านไทยออก
    const blob = new Blob(['﻿' + [head.map(esc).join(','), ...body].join('\r\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `สรุปOT_${period?.from ?? ''}_ถึง_${period?.to ?? ''}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <>
      <h1>สรุป OT</h1>
      <p className="meta">
        ชั่วโมงล่วงเวลารายคนในเดือนที่เลือก แยกตามเรท —
        รวมทั้งใบที่ปิดรอบแล้วและใบที่ยังไม่ปิด
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      <section className="card">
        <SectionHead
          title={data ? `OT ${data.range.from} ถึง ${data.range.to}` : 'สรุป OT'}
          meta={data ? `${nf.format(rows.length)} คน` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setQ(term.trim())
          }}
        >
          <PeriodFilter value={period} onChange={setPeriod} disabled={loading} />
          <label>
            สถานะ
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">ทั้งหมด</option>
              <option value="approved">มีใบที่อนุมัติแล้ว</option>
              <option value="pending">ยังมีใบที่รออยู่</option>
              <option value="rejected">มีใบที่ไม่อนุมัติ</option>
            </select>
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
          {rows.length > 0 && (
            <button type="button" className="ghost" onClick={exportCsv}>
              ดาวน์โหลด CSV
            </button>
          )}
        </form>

        {data && (
          <p className="meta">
            {period?.mode === 'round' && period.roundId && (
              <>
                รอบ <strong>{period.roundId}</strong> ·{' '}
              </>
            )}
            <strong>{nf.format(data.totals.people)}</strong> คน ·{' '}
            {nf.format(data.totals.docs)} ใบ · รวม{' '}
            <strong>{nf.format(data.totals.hours)}</strong> ชั่วโมง · อนุมัติแล้ว{' '}
            <strong>{nf.format(data.totals.approvedHours)}</strong> ชั่วโมง
          </p>
        )}

        {data === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : rows.length === 0 ? (
          <p className="meta">ไม่มี OT ในเดือนนี้</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>ชื่อ</th>
                  <th>ใบ</th>
                  <th>อนุมัติ</th>
                  <th>ชั่วโมงรวม</th>
                  <th>อนุมัติแล้ว</th>
                  <th>x1</th>
                  <th>x1.5</th>
                  <th>x2</th>
                  <th>x3</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.empCode}>
                    <td className="nowrap">{r.empCode}</td>
                    <td>{r.empName}</td>
                    <td>{r.docs}</td>
                    <td>
                      {r.approvedDocs}
                      {r.rejectedDocs > 0 && (
                        <span className="meta"> · ไม่อนุมัติ {r.rejectedDocs}</span>
                      )}
                    </td>
                    <td>
                      <strong>{nf.format(r.hours)}</strong>
                    </td>
                    <td>{nf.format(r.approvedHours)}</td>
                    <td className="meta">{hr(r.byRate['1'])}</td>
                    <td className="meta">{hr(r.byRate['1.5'])}</td>
                    <td className="meta">{hr(r.byRate['2'])}</td>
                    <td className="meta">{hr(r.byRate['3'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="meta">
          ใบร่างที่ช่วงเวลาทับกับใบที่อนุมัติแล้วถูกตัดออก เพราะเป็น OT ครั้งเดียวกัน —
          ไม่งั้นชั่วโมงจะเกินจริง
        </p>
      </section>
    </>
  )
}
